/**
 * sqlite3mt checkpoint quiescence helpers
 *
 * Mechanical extraction from §96 §8.2 and §16.2–§16.3.
 *
 * This module owns stable SHM/WAL snapshot reads plus the control-plane
 * quiescent-window contract that guards explicit checkpoints.
 */

import {
    Sab4Scalar,
    sab4CommitAckSeqView,
    sab4CommitPostSeqView,
    sab4ScalarView,
} from './sab-layout'
import {
    publishCheckpointGateClosed,
    publishCheckpointGateOpen,
    type Deferred,
} from './bootstrap'
import { createDeferred } from './commit-registration'
import {
    abortIfFatal,
    hasAnyOutstandingCommitPosts,
} from './control'
import {
    readWalSnapshotStable as readWalSnapshotStableFromShm,
    sab3ShmInt32View,
} from './shm-ops'
import {
    sameWalSalt,
    type WalSnapshot,
} from './types'

export const INTERNAL_CHECKPOINT_QUIESCENT_REENTRY = 'INTERNAL_CHECKPOINT_QUIESCENT_REENTRY'
export const CHECKPOINT_ORPHANED_UNINTEGRATED_WAL_TAIL = 'CHECKPOINT_ORPHANED_UNINTEGRATED_WAL_TAIL'
export const CHECKPOINT_STUCK_OUTSTANDING_COMMIT_POST = 'CHECKPOINT_STUCK_OUTSTANDING_COMMIT_POST'

export interface CheckpointProbeState {
    frontierValid: boolean
    frontierSalt0: number | null
    frontierSalt1: number | null
    frontierIntegratedFrameMax: number
    liveSalt0: number
    liveSalt1: number
    liveMxFrame: number
}

export interface OutstandingCommitPostState {
    outstanding: ReadonlyArray<{
        connId: number
        post: bigint
        ack: bigint
    }>
}

export interface CheckpointQuiescenceState {
    checkpointQuiescentBusy: boolean
    inflightCommitRegistrations: number
    commitRegistrationDrainWaiter: Deferred<void> | null
    currentWalSalt0: number | null
    currentWalSalt1: number | null
    integratedFrameMax: number
}

export interface CheckpointQuiescenceRuntime {
    syncFatalError: unknown | null
}

export interface CheckpointQuiescenceHooks {
    advanceDbHead(): WalSnapshot
    yieldOneTaskTurnForPendingCommitMessages(): Promise<void>
    enterFatalRuntime(error: unknown, reason: string): void
}

export interface BeginCheckpointQuiescentWindowArgs {
    state: CheckpointQuiescenceState
    runtime: CheckpointQuiescenceRuntime
    sab4: SharedArrayBuffer
    hooks: CheckpointQuiescenceHooks
}

export interface EndCheckpointQuiescentWindowArgs {
    state: Pick<CheckpointQuiescenceState, 'checkpointQuiescentBusy'>
    runtime: CheckpointQuiescenceRuntime
    sab4: SharedArrayBuffer
}

export interface AwaitOutstandingCommitPostsArgs {
    state: Pick<CheckpointQuiescenceState, 'inflightCommitRegistrations'>
    runtime: CheckpointQuiescenceRuntime
    sab4: SharedArrayBuffer
    hooks: Pick<CheckpointQuiescenceHooks, 'yieldOneTaskTurnForPendingCommitMessages' | 'enterFatalRuntime'>
    fatalReason: string
}

export function readWalSnapshotStable(sab3: SharedArrayBuffer): WalSnapshot {
    return readWalSnapshotStableFromShm(sab3ShmInt32View(sab3))
}

export function checkpointTailIsFullyIntegrated(
    state: Pick<CheckpointQuiescenceState, 'currentWalSalt0' | 'currentWalSalt1' | 'integratedFrameMax'>,
    live: WalSnapshot
): boolean {
    if (live.mxFrame === 0) {
        return true
    }
    const effectiveIntegratedFrameMax = Math.max(state.integratedFrameMax, live.nBackfill)
    return state.currentWalSalt0 != null
        && sameWalSalt(state.currentWalSalt0, state.currentWalSalt1, live.salt0, live.salt1)
        && live.mxFrame <= effectiveIntegratedFrameMax
}

export function checkpointProbeState(
    state: Pick<CheckpointQuiescenceState, 'currentWalSalt0' | 'currentWalSalt1' | 'integratedFrameMax'>,
    live: WalSnapshot
): CheckpointProbeState {
    return {
        frontierValid: state.currentWalSalt0 != null,
        frontierSalt0: state.currentWalSalt0,
        frontierSalt1: state.currentWalSalt1,
        frontierIntegratedFrameMax: state.integratedFrameMax,
        liveSalt0: live.salt0,
        liveSalt1: live.salt1,
        liveMxFrame: live.mxFrame,
    }
}

export function sameCheckpointProbeState(
    left: CheckpointProbeState,
    right: CheckpointProbeState
): boolean {
    return left.frontierValid === right.frontierValid
        && left.frontierSalt0 === right.frontierSalt0
        && left.frontierSalt1 === right.frontierSalt1
        && left.frontierIntegratedFrameMax === right.frontierIntegratedFrameMax
        && left.liveSalt0 === right.liveSalt0
        && left.liveSalt1 === right.liveSalt1
        && left.liveMxFrame === right.liveMxFrame
}

export function captureOutstandingCommitPostState(
    sab4: SharedArrayBuffer
): OutstandingCommitPostState {
    const postSeqView = sab4CommitPostSeqView(sab4)
    const ackSeqView = sab4CommitAckSeqView(sab4)
    const outstanding: Array<{ connId: number, post: bigint, ack: bigint }> = []

    for (let connId = 0; connId < postSeqView.length; connId += 1) {
        const post = Atomics.load(postSeqView, connId)
        const ack = Atomics.load(ackSeqView, connId)
        if (post > ack) {
            outstanding.push({ connId, post, ack })
        }
    }

    return {
        outstanding,
    }
}

export function sameOutstandingCommitPostState(
    left: OutstandingCommitPostState,
    right: OutstandingCommitPostState
): boolean {
    if (left.outstanding.length !== right.outstanding.length) {
        return false
    }
    for (let index = 0; index < left.outstanding.length; index += 1) {
        const leftEntry = left.outstanding[index]
        const rightEntry = right.outstanding[index]
        if (leftEntry.connId !== rightEntry.connId
            || leftEntry.post !== rightEntry.post
            || leftEntry.ack !== rightEntry.ack) {
            return false
        }
    }

    return true
}

export async function awaitOutstandingCommitPostsToChangeOrEnterFatal({
    state,
    runtime,
    sab4,
    hooks,
    fatalReason,
}: AwaitOutstandingCommitPostsArgs): Promise<void> {
    if (!hasAnyOutstandingCommitPosts(sab4)) {
        return
    }

    const probe0 = captureOutstandingCommitPostState(sab4)
    await hooks.yieldOneTaskTurnForPendingCommitMessages()
    abortIfFatal(runtime)

    if (Atomics.load(sab4ScalarView(sab4), Sab4Scalar.WAL_WRITE_HOLDERS) > 0
        || state.inflightCommitRegistrations > 0) {
        return
    }

    const probe1 = captureOutstandingCommitPostState(sab4)
    if (probe1.outstanding.length === 0) {
        return
    }
    if (!sameOutstandingCommitPostState(probe0, probe1)) {
        return
    }

    hooks.enterFatalRuntime(fatalReason, fatalReason)
    throw fatalReason
}

export async function beginCheckpointQuiescentWindow({
    state,
    runtime,
    sab4,
    hooks,
}: BeginCheckpointQuiescentWindowArgs): Promise<WalSnapshot> {
    if (state.checkpointQuiescentBusy) {
        throw new Error(INTERNAL_CHECKPOINT_QUIESCENT_REENTRY)
    }

    const scalar = sab4ScalarView(sab4)
    state.checkpointQuiescentBusy = true
    publishCheckpointGateClosed(sab4)

    try {
        for (;;) {
            while (Atomics.load(scalar, Sab4Scalar.WAL_WRITE_HOLDERS) > 0
                || state.inflightCommitRegistrations > 0) {
                if (state.commitRegistrationDrainWaiter == null
                    && state.inflightCommitRegistrations > 0) {
                    state.commitRegistrationDrainWaiter = createDeferred()
                }

                await hooks.yieldOneTaskTurnForPendingCommitMessages()
                abortIfFatal(runtime)
            }

            if (hasAnyOutstandingCommitPosts(sab4)) {
                await awaitOutstandingCommitPostsToChangeOrEnterFatal({
                    state,
                    runtime,
                    sab4,
                    hooks,
                    fatalReason: CHECKPOINT_STUCK_OUTSTANDING_COMMIT_POST,
                })
                abortIfFatal(runtime)
                continue
            }

            const live0 = hooks.advanceDbHead()
            if (checkpointTailIsFullyIntegrated(state, live0)) {
                return live0
            }

            const probe0 = checkpointProbeState(state, live0)
            await hooks.yieldOneTaskTurnForPendingCommitMessages()
            abortIfFatal(runtime)

            if (Atomics.load(scalar, Sab4Scalar.WAL_WRITE_HOLDERS) > 0
                || state.inflightCommitRegistrations > 0) {
                continue
            }

            if (hasAnyOutstandingCommitPosts(sab4)) {
                await awaitOutstandingCommitPostsToChangeOrEnterFatal({
                    state,
                    runtime,
                    sab4,
                    hooks,
                    fatalReason: CHECKPOINT_STUCK_OUTSTANDING_COMMIT_POST,
                })
                abortIfFatal(runtime)
                continue
            }

            const live1 = hooks.advanceDbHead()
            if (checkpointTailIsFullyIntegrated(state, live1)) {
                return live1
            }

            if (!sameCheckpointProbeState(probe0, checkpointProbeState(state, live1))) {
                continue
            }

            hooks.enterFatalRuntime(
                CHECKPOINT_ORPHANED_UNINTEGRATED_WAL_TAIL,
                CHECKPOINT_ORPHANED_UNINTEGRATED_WAL_TAIL
            )
            throw CHECKPOINT_ORPHANED_UNINTEGRATED_WAL_TAIL
        }
    } catch (error) {
        if (runtime.syncFatalError == null) {
            publishCheckpointGateOpen(sab4)
            state.checkpointQuiescentBusy = false
        }
        throw error
    }
}

export function endCheckpointQuiescentWindow({
    state,
    runtime,
    sab4,
}: EndCheckpointQuiescentWindowArgs): void {
    if (runtime.syncFatalError == null
        && Atomics.load(sab4ScalarView(sab4), Sab4Scalar.FATAL_FLAG) === 0) {
        publishCheckpointGateOpen(sab4)
    }
    state.checkpointQuiescentBusy = false
}
